// src/models/porchlightModel.js
import { db } from '../firebase';
// NOTE: Writes to porchlight state are handled via the callable Cloud Function
// 'setPorchlightDuration' to ensure server timestamps, friend visibility,
// and notifications. This module focuses on reading porchlight status
// (subscribeToPorchlightStatus) and client helpers only.
import { doc, onSnapshot, getDoc, collection, query, where } from 'firebase/firestore';

// Returns true if the porchlight is still active (off time is in the future)
export function isPorchlightActive(timestamp) {
  if (!timestamp) return false;
  const date = timestamp.seconds ? 
    new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000) : 
    new Date(timestamp);
  return date > new Date();
}

/**
 * Subscribe to porchlight status changes (reads from 'porchlights/{uid}')
 * @param {string} uid - User ID
 * @param {(data: { until: Date | null, visibleTo?: string[] }) => void} callback - Receives { until, visibleTo }
 * @returns {function} Unsubscribe function
 */
export function subscribeToPorchlightStatus(uid, callback) {
  const docRef = doc(db, 'porchlights', uid);
  return onSnapshot(docRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      const until = data.porchlightUntil || null;
      const visibleTo = Array.isArray(data.visibleTo) ? data.visibleTo : [];
      callback({ until, visibleTo });
    } else {
      callback({ until: null, visibleTo: [] });
    }
  }, (error) => {
    // Provide detailed diagnostics for permission or other failures
    console.error('subscribeToPorchlightStatus onSnapshot error', {
      path: docRef.path,
      code: error.code,
      message: error.message
    });
    // Optionally forward to Sentry here
    // Sentry.captureException(error, { extra: { path: docRef.path } });
  });
}

/**
 * One-time fetch of porchlight status
 * @param {string} uid
 * @returns {Promise<{ until: Date|null, visibleTo: string[], isActive: boolean }>}
 */
export async function getPorchlightStatus(uid) {
  const ref = doc(db, 'porchlights', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { until: null, visibleTo: [], isActive: false };
  }
  const data = snap.data();
  const until = data.porchlightUntil || null;
  const visibleTo = Array.isArray(data.visibleTo) ? data.visibleTo : [];
  return { until, visibleTo, isActive: isPorchlightActive(until) };
}

/**
 * Subscribe to all porchlights visible to the given viewer.
 * Emits a Map of { ownerUid -> until(Date|null) }
 * @param {string} viewerUid
 * @param {(visibleMap: Map<string, Date|null>) => void} callback
 * @returns {function} Unsubscribe function
 */
export function subscribeToVisiblePorchlights(viewerUid, callback) {
  const q = query(collection(db, 'porchlights'), where('visibleTo', 'array-contains', viewerUid));
  return onSnapshot(q, (querySnapshot) => {
    const map = new Map();
    querySnapshot.forEach((docSnap) => {
      const d = docSnap.data() || {};
      const until = d?.porchlightUntil && typeof d.porchlightUntil.toDate === 'function'
        ? d.porchlightUntil.toDate()
        : (d?.porchlightUntil ? new Date(d.porchlightUntil) : null);
      map.set(docSnap.id, until);
    });
    callback(map);
  }, (error) => {
    console.error('subscribeToVisiblePorchlights onSnapshot error', {
      path: 'porchlights where visibleTo contains viewer',
      viewerUid,
      code: error.code,
      message: error.message
    });
    // Sentry.captureException(error, { extra: { viewerUid } });
  });
}

